/*
 * Berry cart drawer — right-side slide-in cart backed by Shopify's AJAX Cart API.
 * /cart.js is the single source of truth. Mirrors the left nav-drawer mechanics
 * (backdrop, body-scroll-lock, Esc/backdrop/X close) on the right. Additive — the
 * /cart page is not touched. One delegated submit listener intercepts every
 * product add-to-cart form (main form, sticky bar, card quick-add) but never the
 * dynamic-checkout button or /checkout links.
 */
(function () {
  var drawer = null;
  var els = {};
  var busy = false;
  var hideTimer = null;
  var moneyFormat = '${{amount}}';
  var lastCart = null;            // freshest /cart.js snapshot (for rec exclusions)
  var lastAddedProductId = null;  // product just added — seeds the recommendations
  var fallbackPool = [];          // server-rendered WHOLE-STORE pool (id/type/cols per product)
  var recCollection = '';         // merchant-set priority fallback collection handle
  // The recommendation fallback chain + variety cap live in the shared
  // window.BerryRecs engine (assets/berry-recommendations.js) — reused verbatim
  // by the product-page related row. This file only seeds it and draws the
  // drawer's own compact cards.

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }

  function init() {
    drawer = qs('[data-cart-drawer]');
    if (!drawer) { return false; }
    els.items = qs('[data-cart-items]', drawer);
    els.empty = qs('[data-cart-empty]', drawer);
    els.footer = qs('[data-cart-footer]', drawer);
    els.subtotal = qs('[data-cart-subtotal]', drawer);
    els.count = qs('[data-cart-drawer-count]', drawer);
    els.error = qs('[data-cart-error]', drawer);
    els.recs = qs('[data-cart-recs]', drawer);
    els.recsRow = qs('[data-cart-recs-row]', drawer);
    els.removeLabel = drawer.getAttribute('data-remove-label') || 'Remove';
    var mf = qs('[data-cart-money-format]', drawer);
    if (mf) { try { moneyFormat = JSON.parse(mf.textContent) || moneyFormat; } catch (e) {} }
    var fb = qs('[data-cart-rec-fallback]', drawer);
    if (fb) { try { fallbackPool = JSON.parse(fb.textContent) || []; } catch (e) { fallbackPool = []; } }
    recCollection = drawer.getAttribute('data-rec-collection') || '';
    return true;
  }

  /* ---- helpers ---- */
  function formatMoney(cents) {
    if (typeof cents === 'string') { cents = cents.replace('.', ''); }
    var rx = /\{\{\s*(\w+)\s*\}\}/;
    function withDelim(num, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(num) || num == null) { num = 0; }
      var n = (num / 100.0).toFixed(precision);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? (decimal + parts[1]) : '';
      return dollars + centsPart;
    }
    var match = moneyFormat.match(rx);
    var token = match ? match[1] : 'amount';
    var value;
    switch (token) {
      case 'amount_no_decimals': value = withDelim(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelim(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelim(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelim(cents, 0, ' ', ''); break;
      default: value = withDelim(cents, 2);
    }
    return moneyFormat.replace(rx, value);
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function imgUrl(url, w) {
    if (!url) { return ''; }
    return url.indexOf('?') === -1 ? url + '?width=' + w : url + '&width=' + w;
  }

  /* ---- open / close (mirrors the nav drawer) ---- */
  function openDrawer() {
    if (!drawer && !init()) { return; }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    drawer.hidden = false;
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.body.classList.add('cd-open');
  }
  function closeDrawer() {
    if (!drawer) { return; }
    drawer.classList.remove('is-open');
    document.body.classList.remove('cd-open');
    hideTimer = setTimeout(function () {
      if (!drawer.classList.contains('is-open')) { drawer.hidden = true; }
    }, 320);
  }

  /* ---- badges + cross-UI event ---- */
  function updateBadges(count) {
    var b = document.querySelectorAll('[data-cart-count]');
    for (var i = 0; i < b.length; i++) {
      b[i].textContent = count;
      if (count > 0) { b[i].removeAttribute('hidden'); } else { b[i].setAttribute('hidden', ''); }
    }
  }
  function dispatchChange(cart) {
    var detail = { cart: cart };
    var ev;
    try { ev = new CustomEvent('cart:change', { detail: detail }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent('cart:change', false, false, detail); }
    document.dispatchEvent(ev);
  }

  /* ---- render ---- */
  function renderItem(item) {
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<div class="cd-item-variant">' + esc(item.variant_title) + '</div>' : '';
    var img = item.image ? '<img src="' + esc(imgUrl(item.image, 160)) + '" alt="" loading="lazy">' : '';
    return '<div class="cd-item" data-cart-line="' + esc(item.key) + '" data-qty="' + item.quantity + '">' +
      '<a class="cd-item-img" href="' + esc(item.url) + '">' + img + '</a>' +
      '<div class="cd-item-main">' +
        '<a class="cd-item-title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
        variant +
        '<div class="cd-item-unit">' + formatMoney(item.final_price) + '</div>' +
        '<div class="cd-item-controls">' +
          '<div class="cd-qty">' +
            '<button type="button" class="cd-qbtn" data-cart-dec aria-label="Decrease quantity">&minus;</button>' +
            '<span class="cd-qval">' + item.quantity + '</span>' +
            '<button type="button" class="cd-qbtn" data-cart-inc aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<button type="button" class="cd-item-remove" data-cart-remove aria-label="Remove ' + esc(item.product_title) + '">' + esc(els.removeLabel) + '</button>' +
        '</div>' +
      '</div>' +
      '<div class="cd-item-line">' + formatMoney(item.final_line_price) + '</div>' +
    '</div>';
  }

  function render(cart) {
    if (!drawer && !init()) { return; }
    lastCart = cart;
    updateBadges(cart.item_count);
    if (els.count) { els.count.textContent = cart.item_count; }
    if (!cart.items || cart.item_count === 0) {
      if (els.items) { els.items.innerHTML = ''; els.items.hidden = true; }
      if (els.empty) { els.empty.hidden = false; }
      if (els.footer) { els.footer.hidden = true; }
      hideRecs();
      return;
    }
    if (els.empty) { els.empty.hidden = true; }
    if (els.footer) { els.footer.hidden = false; }
    if (els.items) {
      els.items.hidden = false;
      var html = '';
      for (var i = 0; i < cart.items.length; i++) { html += renderItem(cart.items[i]); }
      els.items.innerHTML = html;
    }
    if (els.subtotal) { els.subtotal.innerHTML = formatMoney(cart.total_price); }
  }

  function showError(msg) {
    if (!els.error) { return; }
    if (msg) { els.error.textContent = msg; els.error.hidden = false; }
    else { els.error.textContent = ''; els.error.hidden = true; }
  }

  /* ---- "You might also like" recommendations ----
     The fallback chain + variety cap are the shared window.BerryRecs engine
     (assets/berry-recommendations.js), reused verbatim by the product-page
     related row — no second copy. This file only seeds it (product just added +
     cart contents to exclude), then draws the drawer's OWN compact cards. The
     quick-Add on each card is a real <form action="/cart/add"> so it flows
     through the SAME delegated submit handler as every other add. */

  /* width param, but never doubled (fallback urls are already sized in Liquid) */
  function recImg(url, w) {
    if (!url) { return ''; }
    if (/[?&]width=/.test(url)) { return url; }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function hideRecs() {
    if (els.recs) { els.recs.hidden = true; }
    if (els.recsRow) { els.recsRow.innerHTML = ''; }
  }

  function renderRecs(cards) {
    if (!els.recs || !els.recsRow) { return; }
    var html = '';
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      var img = c.image ? '<img src="' + esc(recImg(c.image, 200)) + '" alt="" loading="lazy">' : '';
      html +=
        '<form class="cd-rec" action="/cart/add" method="post" data-cart-rec>' +
          '<a class="cd-rec-media" href="' + esc(c.url) + '" tabindex="-1" aria-hidden="true">' + img + '</a>' +
          '<a class="cd-rec-title" href="' + esc(c.url) + '">' + esc(c.title) + '</a>' +
          '<div class="cd-rec-foot">' +
            '<span class="cd-rec-price">' + formatMoney(c.price) + '</span>' +
            '<input type="hidden" name="id" value="' + esc(c.variant_id) + '">' +
            '<button type="submit" class="cd-rec-add" aria-label="Add ' + esc(c.title) + ' to cart">Add</button>' +
          '</div>' +
        '</form>';
    }
    els.recsRow.innerHTML = html;
    els.recs.hidden = false;
    els.recsRow.scrollLeft = 0;
    /* Reuse the shared drag-scroll hook (no-op if this row is already wired). */
    if (window.BerryDragScroll && typeof window.BerryDragScroll.init === 'function') {
      window.BerryDragScroll.init(els.recsRow);
    }
  }

  function seedFromCart(cart) {
    if (cart && cart.items && cart.items.length) {
      return cart.items[cart.items.length - 1].product_id;
    }
    return null;
  }

  /* Seed the shared BerryRecs engine off the product just added (+ exclude the
     whole cart) and draw the result as compact drawer cards. The engine runs the
     full fallback chain (API related → same-collection → merchant priority
     collection → whole store) with the variety cap, and returns [] when it can't
     reach the minimum → hide the row. loadRecs runs on every add AND every drawer
     open; the engine reshuffles each call → fresh mix each open. */
  function loadRecs(seedId) {
    if (!els.recs || !els.recsRow) { return Promise.resolve(); }
    if (!window.BerryRecs) { hideRecs(); return Promise.resolve(); }
    var cart = lastCart || { items: [] };
    if (!cart.items || cart.item_count === 0) { hideRecs(); return Promise.resolve(); }
    if (!seedId) { seedId = seedFromCart(cart); }
    if (!seedId) { hideRecs(); return Promise.resolve(); }

    var exclude = {};
    for (var i = 0; i < cart.items.length; i++) { exclude[cart.items[i].product_id] = true; }
    exclude[seedId] = true; // the just-added product (already in cart, but be explicit)

    return window.BerryRecs.fetchRelated(seedId).then(function (data) {
      var cards = window.BerryRecs.resolve({
        seedId: seedId,
        exclude: exclude,
        pool: fallbackPool,             // seed's collections resolved from this pool
        recCollection: recCollection,   // merchant priority collection (drawer only)
        apiProducts: data && data.products,
        target: window.BerryRecs.TARGET
      });
      if (!cards.length) { hideRecs(); return; }
      renderRecs(cards);
    });
  }

  /* ---- AJAX Cart API ---- */
  function refresh() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); dispatchChange(cart); return cart; });
  }

  /* The ONE post-add behaviour, shared by every add-to-cart path: (idempotently)
     open the drawer and (re)load a freshly shuffled recommendations row seeded on
     the product just added. Called only from addToCart() below, so any button that
     reaches addToCart automatically gets the drawer + recommendations. openDrawer()
     is idempotent, so adding again while the drawer is already open updates its
     contents in place — it never stacks a second drawer. */
  function afterAdd() {
    openDrawer();
    loadRecs(lastAddedProductId);
  }

  function addToCart(formData) {
    showError('');
    return fetch('/cart/add.js', {
      method: 'POST', headers: { 'Accept': 'application/json' }, body: formData, credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) { return r.json().then(function (err) { throw err; }); }
      return r.json();
    }).then(function (added) {
      // Seed recommendations off the product just added.
      if (added && added.product_id) { lastAddedProductId = added.product_id; }
      else if (added && added.items && added.items[0]) { lastAddedProductId = added.items[0].product_id; }
      return refresh();
    }).then(function () {
      afterAdd();
    }).catch(function (err) {
      return refresh().then(function () {
        afterAdd();
        showError((err && err.description) ? err.description : 'Sorry, we couldn’t add that to your cart.');
      });
    });
  }

  function changeQty(key, qty, lineEl) {
    if (busy) { return; }
    busy = true;
    if (lineEl) {
      lineEl.classList.add('is-loading');
      var lb = lineEl.querySelectorAll('button');
      for (var i = 0; i < lb.length; i++) { lb[i].disabled = true; }
    }
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); })
      .then(function (cart) { render(cart); dispatchChange(cart); })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  /* ---- delegated events (one of each, no duplicates) ---- */
  function step(btn, delta) {
    var li = btn.closest('[data-cart-line]');
    if (!li) { return; }
    var qty = parseInt(li.getAttribute('data-qty'), 10) || 0;
    var next = qty + delta;
    if (next < 0) { next = 0; }
    changeQty(li.getAttribute('data-cart-line'), next, li);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) { return; }
    if (t.closest('[data-cart-toggle]')) {
      e.preventDefault();
      openDrawer();
      refresh().then(function (cart) { loadRecs(lastAddedProductId || seedFromCart(cart)); });
      return;
    }
    if (t.closest('[data-cart-close]')) { e.preventDefault(); closeDrawer(); return; }
    var dec = t.closest('[data-cart-dec]'); if (dec) { step(dec, -1); return; }
    var inc = t.closest('[data-cart-inc]'); if (inc) { step(inc, 1); return; }
    var rem = t.closest('[data-cart-remove]');
    if (rem) {
      var li = rem.closest('[data-cart-line]');
      if (li) { changeQty(li.getAttribute('data-cart-line'), 0, li); }
    }
  });

  /* THE single add-to-cart entry point. One delegated submit listener intercepts
     every product form that posts to /cart/add — no per-button cart logic exists
     anywhere else. Every add path funnels through here → addToCart() → afterAdd():
       • main in-page "Add to cart"  (sections/berry-product.liquid [data-add-btn])
       • sticky mobile bar           (sections/berry-product.liquid [data-sticky-add])
       • product-card quick-adds     (snippets/product-card.liquid .pcard-add — grid,
                                       bestsellers, finish, search, wishlist)
       • the recommendations cards   (rendered here as <form action="/cart/add">)
     Each is a real <button type="submit"> inside a {% form 'product' %}. The
     sticky bar has NO separate add code — it submits the same product form. The
     "Buy it now" dynamic-checkout button is not a /cart/add submit, so it is never
     intercepted here and still goes straight to checkout. A future add button gets
     the drawer + recommendations for free just by being such a form. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.nodeName !== 'FORM') { return; }
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) { return; }
    e.preventDefault();
    var btn = e.submitter || form.querySelector('[type="submit"]');
    if (btn) { btn.disabled = true; btn.classList.add('is-cart-loading'); }
    addToCart(new FormData(form)).then(function () {
      if (btn) { btn.disabled = false; btn.classList.remove('is-cart-loading'); }
    });
  });

  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && drawer && drawer.classList.contains('is-open')) {
      closeDrawer();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
